import React, { useEffect, useState } from 'react';
// @ts-ignore
import styles from './PatientProfileV2.module.scss';
import { useLoadProfile } from './PatientProfileDataLoader';
import { useAppContext } from '../../Services/AppContext';
import Loading from '../Loading';
import FormattedDate from '../FormattedDate';
import FirstUpper from '../FirstUpper';
import PatientUserRelations from '../PatientProfileV4/PatientUserRelations';
import MediaViewerModal from '../MediaViewer/MediaViewerModal';
import useTranslation from '../../Hooks/useTranslation';
import ScrollContainer from '../../Modules/Base/Components/ScrollContainer';
import PatientProfileInformationChildren from './PatientProfileParts/PatientProfileInformationChildren';
import PatientProfileInformation from './PatientProfileParts/PatientProfileInformation';
import PatientProfileEditButton from '../../Modules/EArchive/Components/PatientProfileEdit/PatientProfileEditButton';
import FormattedMomentDate, {
	DateFormat,
} from '../../Modules/Base/Components/FormattedMomentDate/FormattedMomentDate';
import PatientProfileEditableImage from '../../Modules/Patient/Components/PatientProfileImageEdit/PatientProfileEditableImage';

interface PatientProfileV2Props {
	patientId: string;
	verifyInsurance?: () => void;
	verifyAddress?: () => void;
	reloadDeps?: any;
	setIsLoading: (isLoading: boolean) => void;
}

export default function PatientProfileV2(props: PatientProfileV2Props) {
	const { patientId, verifyInsurance, verifyAddress, setIsLoading } = props;
	const { translation, globalTemplate } = useAppContext();
	const [mediaViewerOpened, setMediaViewerOpened] = useState(false);
	const [insuranceCardFile, setInsuranceCardFile] = useState(null);
	const showCzechServices = globalTemplate === 'default';
	const { t } = useTranslation();
	const [isShowPatientId, setIsShowPatientId] = useState(false);
	const [secretCounter, setSecretCounter] = useState(0);

	const {
		user,
		psychoQuestionnaire,
		psychoQuestionnaireTheme,
		doctorsData,
		insuranceCard,
		userRelations,
		isLoading,
		reload,
	} = useLoadProfile({
		userId: patientId,
		loadVerifyInformation: showCzechServices,
		loadUserRelations: false,
		loadHealthCard: true,
	});

	useEffect(() => {
		setIsLoading(isLoading);
	}, [isLoading]);

	useEffect(() => {
		if (props.reloadDeps) {
			reload();
		}
	}, [props.reloadDeps]);

	if (!user) {
		return (
			<div className={styles.wrapper}>
				<div className={'py-5'}>
					<Loading />
				</div>
			</div>
		);
	}

	const handleOpenInsuranceCard = (image: string) => {
		const fileToViewer = {
			id: 'insurance-file',
			name: t('base.shared.insurenceCard'),
			displayName: t('base.shared.insurenceCard'),
			type: 'image',
			preview: image,
			download: image,
		};

		setInsuranceCardFile(fileToViewer);
		setMediaViewerOpened(true);
	};

	const handleSecretClick = () => {
		if (secretCounter > 9) {
			//setIsShowPatientId(true);
		}
		setSecretCounter(prev => prev + 1);
	};

	const handleAfterProfileEditSuccess = () => {
		reload();
	};

	return (
		<ScrollContainer>
			<div className={styles.wrapper}>
				<div className={styles.basicData}>
					<div className={styles.col}>
						<PatientProfileEditableImage
							user={user}
							className={styles.profileImage}
							onProfileImageChange={reload}
						/>
					</div>
					<div className={styles.col} style={{ flexGrow: 1 }}>
						<div className='d-flex w-100 justify-content-between'>
							<div className={styles.title}>
								{user.profile.fullName}
								{isShowPatientId && <span>({user.id})</span>}
								{user.workplaceMember?.dateOfDeath && (
									<div className={styles.dateOfDeath}>
										<img
											className={styles.cross}
											src='/dist/icons/cross-sharp-solid.svg'
											alt='cross'
										/>
										<FormattedMomentDate
											date={user.workplaceMember.dateOfDeath}
											format={DateFormat.DATE}
										/>
									</div>
								)}
							</div>
							<PatientProfileEditButton
								user={user}
								userId={user.id}
								onSuccess={handleAfterProfileEditSuccess}
							/>
						</div>
						{user?.isChild ? (
							<PatientProfileInformationChildren
								user={user}
								showCzechServices={showCzechServices}
								onProfileEdit={handleAfterProfileEditSuccess}
							/>
						) : (
							<PatientProfileInformation
								user={user}
								showCzechServices={showCzechServices}
								psychoQuestionnaire={psychoQuestionnaire}
								psychoQuestionnaireTheme={psychoQuestionnaireTheme}
								setIsLoading={setIsLoading}
							/>
						)}
					</div>
					<div className={styles.hiddenButton} onClick={handleSecretClick} />
				</div>
				{showCzechServices && doctorsData && !!doctorsData.length && (
					<div className={styles.doctors}>
						<div className={styles.title}>
							<FirstUpper>{translation['base.registerinDoctors']}</FirstUpper>
						</div>
						{doctorsData.map((item, index) => {
							return (
								<div className={styles.item} key={index}>
									<div className={styles.name}>
										<FormattedDate date={item.dateFrom} /> -{' '}
										<FormattedDate date={item.dateTo} />
									</div>
									<div className={styles.text}>
										{item.expertise.code} - {item.expertise.name}
										<br />
										{translation['component.generalForm.icp']}{' '}
										{item.workplace.icp} — {item.workplace.name}
										<br />
										{translation['component.generalForm.icz']}{' '}
										{item.workplace.icz} — {item.workplace.nameSZZ}
										<br />
										<FirstUpper>
											{translation['base.insuranceShort']}
										</FirstUpper>
										: {item.insuranceCompany.code} -{' '}
										{item.insuranceCompany.value}
										<br />
										<br />
									</div>
								</div>
							);
						})}
					</div>
				)}
				{user.partner == 3 && userRelations && (
					<PatientUserRelations
						userId={patientId}
						v2template={true}
						userRelations={userRelations}
					/>
				)}
				{(insuranceCard?.front || insuranceCard?.back) && (
					<div className={styles.healthCard}>
						<div className={styles.title}>
							<FirstUpper>
								{translation['base.shared.insuranceCard']}
							</FirstUpper>
						</div>
						{insuranceCard.front && (
							<div>
								<img
									className={styles.card}
									src={insuranceCard.front}
									alt={'insurance card front'}
									onClick={() => handleOpenInsuranceCard(insuranceCard.front)}
								/>
							</div>
						)}
						{insuranceCard.back && (
							<div>
								<img
									className={styles.card}
									src={insuranceCard.back}
									alt={'insurance card back'}
									onClick={() => handleOpenInsuranceCard(insuranceCard.back)}
								/>
							</div>
						)}
					</div>
				)}
				<MediaViewerModal
					opened={mediaViewerOpened}
					onClose={() => setMediaViewerOpened(false)}
					file={insuranceCardFile}
				/>
			</div>
		</ScrollContainer>
	);
}
